/**
 * The first page of a print sheet, drawn in millimetres: the paper, then the photo in every cell (cut to the cell,
 * turned 90° where the layout turns it), then the cut marks the PDF gets (sheet.ts cutMarks). The same preview in both
 * modes (merged page, GĐ2): "Tải tờ in PDF" of an ID photo (sheet.ts) and "Xếp tờ in" of the editor
 * (editor/printLayout.ts).
 */
import type { Segment } from "../sheet";

interface PreviewCell {
  x: number;
  y: number;
  w: number;
  h: number;
  turned?: boolean;
  /** GĐ4: which of `photos` goes in this cell */
  image?: number;
}

interface PreviewPhoto {
  url: string;
  width: number;
  height: number;
  rect?: number[];
}

interface Props {
  paper: [number, number];
  cells: PreviewCell[];
  label: string;
  /** the photo in the cells; `rect` = the part of it that is printed (fractions), default the whole */
  photo?: PreviewPhoto | null;
  /** GĐ4: several photos (one per spec), picked per cell by `image` */
  photos?: PreviewPhoto[];
  /** the cut marks (mm), as printed */
  marks?: Segment[];
}

export function SheetPreview({ paper, cells, label, photo, photos, marks }: Props) {
  return (
    <svg className="sheet-preview" viewBox={`0 0 ${paper[0]} ${paper[1]}`} role="img" aria-label={label}>
      <rect x="0" y="0" width={paper[0]} height={paper[1]} className="sheet-paper" />
      {cells.map((c, i) => {
        const shown = photos ? photos[c.image ?? 0] : photo;
        return <Cell key={i} c={c} photo={shown ?? null} />;
      })}
      {marks && marks.length > 0 && (
        <path className="sheet-marks" d={marks.map(([x1, y1, x2, y2]) => `M${x1} ${y1}L${x2} ${y2}`).join("")} />
      )}
    </svg>
  );
}

function Cell({ c, photo }: { c: PreviewCell; photo: PreviewPhoto | null }) {
  const rect = photo?.rect ?? [0, 0, 1, 1];
  return (
        <g>
          {photo ? (
            <g transform={c.turned ? `rotate(90 ${c.x + c.w / 2} ${c.y + c.h / 2})` : undefined}>
              <svg x={c.turned ? c.x + (c.w - c.h) / 2 : c.x} y={c.turned ? c.y + (c.h - c.w) / 2 : c.y}
                   width={c.turned ? c.h : c.w} height={c.turned ? c.w : c.h} preserveAspectRatio="xMidYMid slice"
                   viewBox={`${rect[0] * photo.width} ${rect[1] * photo.height} ${rect[2] * photo.width} ${rect[3] * photo.height}`}>
                <image href={photo.url} width={photo.width} height={photo.height} preserveAspectRatio="none" />
              </svg>
            </g>
          ) : <rect x={c.x} y={c.y} width={c.w} height={c.h} className="sheet-photo" />}
          <rect x={c.x} y={c.y} width={c.w} height={c.h} className="sheet-cell" />
        </g>
  );
}
